import { useEffect, useState, useMemo, type CSSProperties, type FormEvent } from "react";
import {
  listBusinessEvents,
  saveBusinessEvent,
  deleteBusinessEvent,
  type BusinessEvent,
} from "../../lib/api";
import OperationsStudioTabs from "../../components/admin/OperationsStudioTabs";

const EVENT_TYPES = [
  { id: "all", label: "All Types" },
  { id: "sports", label: "Chiefs / Sporting KC", color: "#E05040" },
  { id: "concert", label: "Arena Concert", color: "#9B59B6" },
  { id: "convention", label: "Convention / Expo", color: "#4A8FD9" },
  { id: "holiday", label: "Holiday Weekend", color: "var(--gold)" },
  { id: "weather", label: "Extreme Weather", color: "#64a0f0" },
  { id: "school", label: "School / Graduation", color: "#E67E22" },
  { id: "local", label: "Local Event / Festival", color: "#27ae60" },
];

export default function AdminBusinessContext() {
  const [events, setEvents] = useState<BusinessEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Editor Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Partial<BusinessEvent> | null>(null);
  const [saving, setSaving] = useState(false);

  // Toast
  const [toast, setToast] = useState<{ text: string; type: "ok" | "err" } | null>(null);
  const showToast = (text: string, type: "ok" | "err" = "ok") => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Escape key closes modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isModalOpen) setIsModalOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isModalOpen]);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await listBusinessEvents();
      setEvents(data || []);
    } catch (err: any) {
      showToast(err.message || "Failed to load business events", "err");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredEvents = useMemo(() => {
    let list = events;
    if (typeFilter !== "all") {
      list = list.filter((e) => (e.event_type || "").toLowerCase() === typeFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (e) =>
          e.event_name.toLowerCase().includes(q) ||
          (e.notes && e.notes.toLowerCase().includes(q))
      );
    }
    return list;
  }, [events, typeFilter, searchQuery]);

  const handleNewEvent = () => {
    setEditingEvent({
      event_date: new Date().toISOString().split("T")[0],
      event_name: "",
      event_type: "sports",
      expected_impact: "high_positive",
      notes: "",
    });
    setIsModalOpen(true);
  };

  const handleEditEvent = (ev: BusinessEvent) => {
    setEditingEvent({ ...ev });
    setIsModalOpen(true);
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingEvent || !editingEvent.event_name) return;
    setSaving(true);
    try {
      await saveBusinessEvent(editingEvent);
      showToast("✓ Business event saved");
      setIsModalOpen(false);
      await loadData();
    } catch (err: any) {
      showToast(err.message || "Failed to save event", "err");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
    try {
      await deleteBusinessEvent(id);
      showToast(`✓ Removed "${name}"`);
      setIsModalOpen(false);
      await loadData();
    } catch (err: any) {
      showToast(err.message || "Failed to delete event", "err");
    }
  };

  return (
    <div style={styles.container}>
      <OperationsStudioTabs activeTab="context" badgeCounts={{ context: events.length }} />
      {toast && (
        <div style={{ ...styles.toast, background: toast.type === "err" ? "#E05040" : "var(--gold)" }}>
          {toast.text}
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Business Context &amp; Demand Factors</h1>
          <p style={styles.subtitle}>
            Log external events, sports games, conventions, and severe weather that drive or impact bar sales volume.
          </p>
        </div>
        <button type="button" style={styles.btnPrimary} onClick={handleNewEvent}>
          + Log External Event
        </button>
      </div>

      {/* Toolbar */}
      <div style={styles.toolbar}>
        <input
          type="text"
          placeholder="Search by event name, notes, date…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchInput}
        />
        <div style={{ display: "flex", gap: 8 }}>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            style={styles.select}
          >
            {EVENT_TYPES.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Events Timeline List */}
      {loading ? (
        <div style={styles.loader}>Loading business events…</div>
      ) : filteredEvents.length === 0 ? (
        <div style={styles.emptyState}>No external business events logged.</div>
      ) : (
        <div style={styles.eventsGrid}>
          {filteredEvents.map((e) => {
            const evType = EVENT_TYPES.find((t) => t.id === e.event_type) || EVENT_TYPES[1];
            const isPos = e.expected_impact?.includes("positive");
            const isNeg = e.expected_impact?.includes("negative");
            return (
              <div
                key={e.id}
                style={styles.card}
                onClick={() => handleEditEvent(e)}
              >
                <div style={styles.cardHeader}>
                  <div style={styles.dateBadge}>
                    {new Date(e.event_date + "T12:00:00").toLocaleDateString("en-US", {
                      weekday: "short",
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </div>
                  <span
                    style={{
                      ...styles.typeBadge,
                      background: `${evType.color}22`,
                      color: evType.color,
                    }}
                  >
                    {evType.label.split(" ")[0]}
                  </span>
                </div>

                <h3 style={styles.cardName}>{e.event_name}</h3>
                {e.notes && <p style={styles.cardNotes}>{e.notes}</p>}

                <div style={styles.cardFooter}>
                  <span
                    style={{
                      ...styles.impactBadge,
                      color: isPos ? "#27ae60" : isNeg ? "#E05040" : "var(--muted)",
                    }}
                  >
                    Impact: {e.expected_impact?.replace("_", " ").toUpperCase() || "NEUTRAL"}
                  </span>
                  <span style={{ color: "var(--gold)", fontWeight: 700, fontSize: 11 }}>Edit →</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && editingEvent && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {editingEvent.id ? "Edit External Event" : "Log External Event"}
              </h2>
              <button
                type="button"
                style={styles.btnIcon}
                onClick={() => setIsModalOpen(false)}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSave} style={styles.modalForm}>
              <div className="station-form-grid" style={styles.formGrid}>
                <div style={{ ...styles.fieldGroup, gridColumn: "1 / -1" }}>
                  <label style={styles.label}>Event Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Chiefs vs Broncos (Sunday Night Football)"
                    value={editingEvent.event_name || ""}
                    onChange={(e) =>
                      setEditingEvent({ ...editingEvent, event_name: e.target.value })
                    }
                    style={styles.input}
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Event Date *</label>
                  <input
                    type="date"
                    required
                    value={editingEvent.event_date || ""}
                    onChange={(e) =>
                      setEditingEvent({ ...editingEvent, event_date: e.target.value })
                    }
                    style={styles.input}
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Event Category</label>
                  <select
                    value={editingEvent.event_type || "sports"}
                    onChange={(e) =>
                      setEditingEvent({ ...editingEvent, event_type: e.target.value })
                    }
                    style={styles.select}
                  >
                    {EVENT_TYPES.filter((t) => t.id !== "all").map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ ...styles.fieldGroup, gridColumn: "1 / -1" }}>
                  <label style={styles.label}>Expected Sales Impact</label>
                  <select
                    value={editingEvent.expected_impact || "high_positive"}
                    onChange={(e) =>
                      setEditingEvent({ ...editingEvent, expected_impact: e.target.value })
                    }
                    style={styles.select}
                  >
                    <option value="high_positive">High Positive (+30% to +50% volume)</option>
                    <option value="moderate_positive">Moderate Positive (+10% to +25% volume)</option>
                    <option value="neutral">Neutral (Standard volume)</option>
                    <option value="negative">Negative (Slow night / Ice storm)</option>
                  </select>
                </div>

                <div style={{ ...styles.fieldGroup, gridColumn: "1 / -1" }}>
                  <label style={styles.label}>Staffing &amp; Prep Notes</label>
                  <textarea
                    rows={3}
                    placeholder="Anticipate heavy late-night influx post-game. Extra batch prep on Old Fashioneds..."
                    value={editingEvent.notes || ""}
                    onChange={(e) =>
                      setEditingEvent({ ...editingEvent, notes: e.target.value })
                    }
                    style={styles.textarea}
                  />
                </div>
              </div>

              <div style={styles.modalActions}>
                {editingEvent.id && (
                  <button
                    type="button"
                    style={styles.btnDanger}
                    onClick={() =>
                      handleDelete(editingEvent.id!, editingEvent.event_name || "Event")
                    }
                  >
                    Delete Event
                  </button>
                )}
                <div style={{ display: "flex", gap: 10, marginLeft: "auto" }}>
                  <button
                    type="button"
                    style={styles.btnSecondary}
                    onClick={() => setIsModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" style={styles.btnPrimary} disabled={saving}>
                    {saving ? "Saving…" : "Save Event"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: { padding: "20px 24px", maxWidth: 1200, margin: "0 auto" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 700, margin: 0, color: "var(--text)" },
  subtitle: { fontSize: 13, color: "var(--muted)", margin: "4px 0 0" },
  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 20 },
  searchInput: { flex: 1, minWidth: 260, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "8px 12px", fontSize: 13 },
  select: { background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "8px 12px", fontSize: 13 },
  eventsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 },
  card: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 16, cursor: "pointer", display: "flex", flexDirection: "column", gap: 8 },
  cardHeader: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  dateBadge: { fontSize: 12, fontWeight: 700, color: "var(--gold)" },
  typeBadge: { fontSize: 10, fontWeight: 800, textTransform: "uppercase", padding: "2px 8px", borderRadius: 4, letterSpacing: 0.5 },
  cardName: { fontSize: 16, fontWeight: 700, margin: 0, color: "var(--text)" },
  cardNotes: { fontSize: 12, color: "var(--muted)", margin: 0, lineHeight: 1.4 },
  cardFooter: { display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid var(--border)", paddingTop: 10, marginTop: "auto" },
  impactBadge: { fontSize: 11, fontWeight: 800 },
  btnPrimary: { background: "var(--gold)", color: "#111", border: 0, padding: "8px 16px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer" },
  btnSecondary: { background: "transparent", color: "var(--text)", border: "1px solid var(--border)", padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" },
  btnDanger: { background: "transparent", color: "#E05040", border: "1px solid rgba(224,80,64,0.4)", padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer" },
  btnIcon: { background: "transparent", border: 0, color: "var(--muted)", fontSize: 22, cursor: "pointer" },
  loader: { padding: 40, textAlign: "center", color: "var(--muted)", fontSize: 14 },
  emptyState: { padding: 40, textAlign: "center", color: "var(--muted)", fontSize: 13 },
  toast: { position: "fixed", bottom: 20, right: 20, color: "#111", fontWeight: 700, padding: "10px 18px", borderRadius: 8, zIndex: 9999, fontSize: 13, boxShadow: "0 4px 12px rgba(0,0,0,0.4)" },
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 },
  modalContent: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, width: "100%", maxWidth: 600, maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid var(--border)" },
  modalTitle: { fontSize: 18, fontWeight: 700, color: "var(--text)", margin: 0 },
  modalForm: { padding: 20, overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 16 },
  formGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 },
  fieldGroup: { display: "flex", flexDirection: "column", gap: 4 },
  label: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--muted)", letterSpacing: 0.5 },
  input: { background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "8px 12px", fontSize: 13 },
  textarea: { width: "100%", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "10px 12px", fontSize: 13, resize: "vertical" },
  modalActions: { display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid var(--border)", paddingTop: 16 },
};
